import { api } from '@/contexts/AuthContext';
import {
  ConnectedSheet,
  AvailableSheet,
  SheetsPagination,
  GoogleAuthStatus,
} from '@/types';

type FetchOptions = { force?: boolean };

function dedupeInflight<T>(
  slot: { current: Promise<T> | null },
  force: boolean | undefined,
  factory: () => Promise<T>
): Promise<T> {
  if (!force && slot.current) return slot.current;
  const promise = factory().finally(() => {
    if (slot.current === promise) slot.current = null;
  });
  slot.current = promise;
  return promise;
}

const connectedSheetsInflight: { current: Promise<ConnectedSheet[]> | null } = { current: null };
const authStatusInflight: { current: Promise<GoogleAuthStatus> | null } = { current: null };
const availableSheetsInflight = new Map<string, Promise<{
  sheets: AvailableSheet[];
  pagination: SheetsPagination;
  connected_count: number;
}>>();
const pickerConfigInflight: { current: Promise<PickerConfigResponse> | null } = { current: null };

export type PickerConfigResponse = {
  api_key: string;
  client_id: string;
  app_id: string;
  access_token: string;
  scopes: string[];
  picker_enabled: boolean;
};

export type SheetTab = {
  sheet_id: number;
  title: string;
  index: number;
  hidden: boolean;
};

export function getConnectedSheets(options?: FetchOptions): Promise<ConnectedSheet[]> {
  return dedupeInflight(connectedSheetsInflight, options?.force, async () => {
    const response = await api.get<ConnectedSheet[]>('/google-sheets');
    return response.data;
  });
}

export function getGoogleSheetsAuthStatus(options?: FetchOptions): Promise<GoogleAuthStatus> {
  return dedupeInflight(authStatusInflight, options?.force, async () => {
    const response = await api.get<GoogleAuthStatus>('/google-sheets/auth-status');
    return response.data;
  });
}

export function getAvailableSheets(
  page = 1,
  limit = 6,
  options?: FetchOptions
): Promise<{
  sheets: AvailableSheet[];
  pagination: SheetsPagination;
  connected_count: number;
}> {
  const key = `${page}:${limit}`;
  if (!options?.force) {
    const existing = availableSheetsInflight.get(key);
    if (existing) return existing;
  }

  const promise = api
    .get<{
      sheets: AvailableSheet[];
      pagination: SheetsPagination;
      connected_count: number;
    }>(`/google-sheets/available?page=${page}&limit=${limit}`)
    .then((response) => response.data)
    .finally(() => {
      if (availableSheetsInflight.get(key) === promise) {
        availableSheetsInflight.delete(key);
      }
    });

  availableSheetsInflight.set(key, promise);
  return promise;
}

export function getGooglePickerConfig(options?: FetchOptions): Promise<PickerConfigResponse> {
  return dedupeInflight(pickerConfigInflight, options?.force, async () => {
    const response = await api.get<PickerConfigResponse>('/google-picker/config');
    return response.data;
  });
}

export async function connectGoogleSheet(sheetId: string): Promise<void> {
  await api.post('/google-sheets/connect', {
    sheet_id: sheetId,
  });
}

export async function getGoogleSheetTabs(sheetId: string): Promise<SheetTab[]> {
  const response = await api.get<{ tabs: SheetTab[] }>(
    `/google-sheets/${sheetId}/tabs`
  );

  return response.data.tabs || [];
}

export async function refreshAvailableGoogleSheets(): Promise<void> {
  await api.post('/google-sheets/load-available-direct', {});
}

export async function createGoogleSheet(name: string): Promise<any> {
  const response = await api.post('/google-sheets/create', {
    name,
  });

  return response.data;
}

export async function getGoogleSheetsAuthorizationUrl(
  context: string
): Promise<string | null> {
  const response = await api.get<{ authorization_url?: string }>(
    '/google-sheets/auth',
    {
      params: { context },
    }
  );

  return response.data.authorization_url ?? null;
}
